<template>
    <div class="setup-container">
        <div class="page-hero">
            <div>
                <p class="eyebrow">Permissions</p>
                <h1>Assign Module</h1>
                <p class="hero-copy">Connect modules to roles so each role gets the correct navigation access.</p>
            </div>
            <button class="setup-btn" @click="loadData">
                <RefreshCcw class="icon" />
                Refresh
            </button>
        </div>

        <section class="panel">
            <form class="setup-form" @submit.prevent="submitForm">
                <div class="form-group">
                    <label for="roleId">Role</label>
                    <select id="roleId" v-model="form.roleId" required>
                        <option value="" disabled>Select role</option>
                        <option v-for="role in roles" :key="role.RoleId" :value="role.RoleId">
                            {{ role.RoleName }}
                        </option>
                    </select>
                </div>

                <div class="form-group">
                    <label for="routeId">Module</label>
                    <div class="checkbox-group" role="group" aria-labelledby="routeId">
                        <label
                            v-for="route in routes"
                            :key="route.RouteId"
                            class="checkbox-item"
                        >
                            <input
                                type="checkbox"
                                v-model="form.routeIds"
                                :value="route.RouteId"
                            />
                            <span>{{ route.RouteName }}</span>
                        </label>
                    </div>
                    <small class="field-hint">
                        {{ form.routeIds.length ? `${form.routeIds.length} module(s) selected` : "Select one or more modules" }}
                    </small>
                </div>

                <div class="buttons mt-3">
                    <button type="submit">Assign Module</button> &nbsp;
                    <button type="button" @click="resetForm">Reset</button>
                </div>
            </form>
        </section>

        <section class="panel">
            <div class="header-search">
                <div>
                    <p class="section-kicker">Assignments</p>
                    <h2>Role modules</h2>
                </div>
                <div class="search-bar">
                    <Search />
                    <input v-model="searchQuery" type="text" placeholder="Search assignments..." />
                </div>
            </div>

            <table class="table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Role</th>
                        <th>Module</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="paginatedAssignments.length === 0">
                        <td colspan="3" class="no-data">No module assignments found.</td>
                    </tr>
                    <tr v-else v-for="(item, index) in paginatedAssignments" :key="`${item.RoleId}-${item.RouteId}`">
                        <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                        <td>{{ item.RoleName || `Role #${item.RoleId}` }}</td>
                        <td>{{ item.RouteName || `Route #${item.RouteId}` }}</td>
                    </tr>
                </tbody>
            </table>

            <div class="pagination">
                <button @click="prevPage" :disabled="currentPage === 1">
                    <ChevronLeft />
                </button>
                <button
                    v-for="page in totalPages"
                    :key="page"
                    @click="goToPage(page)"
                    :class="{ active: currentPage === page }"
                >
                    {{ page }}
                </button>
                <button @click="nextPage" :disabled="currentPage === totalPages">
                    <ChevronRight />
                </button>
            </div>
        </section>
    </div>
</template>

<script setup>
import { ChevronLeft, ChevronRight, RefreshCcw, Search } from "lucide-vue-next";
import { computed, onMounted, ref, watch } from "vue";
import axios from "axios";

const rolesApi = "/roles_lists.php";
const routesApi = "/routes_lists.php";
const assignmentsApi = "/role_routes_lists.php";
const createApi = "/role_routes_setup.php";

const roles = ref([]);
const routes = ref([]);
const assignments = ref([]);
const searchQuery = ref("");
const currentPage = ref(1);
const pageSize = 8;

const form = ref({
    roleId: "",
    routeIds: []
});

const filteredAssignments = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return assignments.value;

    return assignments.value.filter((item) => {
        return (
            String(item.RoleName || "").toLowerCase().includes(query) ||
            String(item.RouteName || "").toLowerCase().includes(query) ||
            String(item.RoutePath || "").toLowerCase().includes(query)
        );
    });
});

const totalPages = computed(() => Math.max(1, Math.ceil(filteredAssignments.value.length / pageSize)));

const paginatedAssignments = computed(() => {
    const start = (currentPage.value - 1) * pageSize;
    return filteredAssignments.value.slice(start, start + pageSize);
});

watch(searchQuery, () => {
    currentPage.value = 1;
});

function goToPage(page) {
    if (page >= 1 && page <= totalPages.value) currentPage.value = page;
}

function prevPage() {
    if (currentPage.value > 1) currentPage.value--;
}

function nextPage() {
    if (currentPage.value < totalPages.value) currentPage.value++;
}

function resetForm() {
    form.value.roleId = "";
    form.value.routeIds = [];
}

function syncRoleModules(roleId) {
    if (!roleId) {
        form.value.routeIds = [];
        return;
    }

    const selectedRoleRoutes = assignments.value
        .filter((item) => String(item.RoleId) === String(roleId))
        .map((item) => item.RouteId);

    form.value.routeIds = [...new Set(selectedRoleRoutes)];
}

watch(
    () => form.value.roleId,
    (roleId) => {
        syncRoleModules(roleId);
    }
);

async function loadData() {
    try {
        const [rolesResponse, routesResponse, assignmentsResponse] = await Promise.all([
            axios.get(rolesApi),
            axios.get(routesApi),
            axios.get(assignmentsApi)
        ]);

        roles.value = rolesResponse?.data?.data ?? [];
        routes.value = routesResponse?.data?.data ?? [];
        assignments.value = assignmentsResponse?.data?.data ?? [];
        syncRoleModules(form.value.roleId);
    } catch (error) {
        console.error(error);
    }
}

async function submitForm() {
    if (!form.value.routeIds.length) {
        alert("Please select at least one module.");
        return;
    }

    try {
        const payload = {
            RoleId: form.value.roleId,
            RouteId: form.value.routeIds
        };

        const response = await axios.post(createApi, payload, {
            headers: { "Content-Type": "application/json" }
        });

        alert(response?.data?.message || "Route assigned successfully.");
        resetForm();
        await loadData();
    } catch (error) {
        alert(error?.response?.data?.message || error.message);
    }
}

onMounted(loadData);
</script>

<style scoped>
.checkbox-group {
    display: grid;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    gap: 10px;
    width: 100%;
    padding: 10px 0 4px;
}

.checkbox-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 12px;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.checkbox-item:hover {
    border-color: #ed4e24;
    box-shadow: 0 0 0 2px rgba(237, 78, 36, 0.08);
}

.checkbox-item input {
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: #ed4e24;
}

.field-hint {
    display: inline-block;
    margin-top: 6px;
    color: #6b7280;
    font-size: 12px;
}

@media (min-width: 768px) {
    .checkbox-group {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (min-width: 1100px) {
    .checkbox-group {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}
</style>
